import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleUrlChange, setPageType, setDataLoading } from '../redux/slices/filterSlice';
import debounce from 'lodash.debounce';

export const useUrlChangeHandler = () => {
  const dispatch = useDispatch();
  const searchTerm = useSelector(state => state.filter.searchTerm);
  const sortBy = useSelector(state => state.filter.sortBy);
  const filters = useSelector(state => state.filter.filters);
  const originalPushState = useRef(null);
  const isInitialMount = useRef(true);
  const hasInitialized = useRef(false);
  const lastRequestedParams = useRef(null);

  // Memoize getPageType to ensure consistent reference
  const getPageType = useMemo(() => () => {
    const path = window.location.pathname;
    if (path === '/browse') return 'browse';
    if (path === '/dashboard/listings') return 'listings';
    if (path === '/dashboard/transactions') return 'transactions';
    if (path === '/favorites') return 'favorites';
    if (path.includes('/listing/')) return 'listingForm';
    if (path.includes('/merchant/')) {
      return 'merchant';
    }
    return null;
  }, []);

  // Memoize the current page type
  const currentPageType = useMemo(() => getPageType(), [getPageType]);

  // Memoize dispatch handlers to prevent recreation.
  //
  // This must not depend on any changing slice state. It used to read
  // `dataLoading` and clear it here, which made the callback — and therefore
  // the mount effect below, which resets its own "already initialized" refs on
  // cleanup — churn on every fetch. Starting a page-2 fetch flipped
  // `dataLoading`, which re-ran the effect, which cleared `dataLoading` while
  // the request was still in flight (killing the bottom spinner and re-arming
  // the sentinel for a duplicate fetch) and re-issued the initial search,
  // replacing the whole grid mid-scroll.
  //
  // Clearing the loading flag here is also unnecessary: `setPageType` resets
  // the entire slice whenever the page type actually changes.
  const handlePageTypeChange = useCallback((pageType) => {
    if (pageType) {
      dispatch(setPageType(pageType));
    }
  }, [dispatch]);

  const handleSearch = useCallback((pageType) => {
    if (pageType) {
      dispatch(handleUrlChange());
    }
  }, [dispatch]);

  // Memoize debounced search to maintain stable reference
  const debouncedSearch = useMemo(
    () => debounce(handleSearch, 300),
    [handleSearch]
  );

  // Initial setup effect - runs once
  useEffect(() => {
    // Handle initial mount
    if (isInitialMount.current) {
      handlePageTypeChange(currentPageType);
      if (!hasInitialized.current) {
        debouncedSearch(currentPageType);
        hasInitialized.current = true;
      }
      isInitialMount.current = false;
    }

    // Cleanup
    return () => {
      handlePageTypeChange(null);
      isInitialMount.current = true;
      hasInitialized.current = false;
      lastRequestedParams.current = null;
      debouncedSearch.cancel();
    };
  }, [currentPageType, handlePageTypeChange, debouncedSearch]);

  // Search whenever the filter parameters change.
  //
  // The filter controls dispatch setSearchTerm/setSortBy/setFilter and push the
  // new URL, but nothing on this page listens to `pushState` for a same-page
  // navigation — so no request was ever made from here. Filtering only appeared
  // to work because the effect above used to churn on every `dataLoading` flip
  // and re-issue the initial search as a side effect. That accident is also
  // what re-ran the initial search mid-pagination and replaced the grid, so the
  // reaction has to be explicit instead.
  //
  // Compared as a serialized value, never by object identity: every search
  // merges a fresh `filters` object into the slice, so an identity dependency
  // would fetch in a loop.
  const paramsKey = useMemo(
    () => JSON.stringify({ searchTerm, sortBy, filters }),
    [searchTerm, sortBy, filters]
  );

  useEffect(() => {
    if (!currentPageType) return;

    // The mount effect owns the first search; only react to later changes.
    if (lastRequestedParams.current === null) {
      lastRequestedParams.current = paramsKey;
      return;
    }

    if (lastRequestedParams.current === paramsKey) return;
    lastRequestedParams.current = paramsKey;

    debouncedSearch(currentPageType);
  }, [paramsKey, currentPageType, debouncedSearch]);

  // URL change handling effect - runs once
  useEffect(() => {

    if (!originalPushState.current) {
      originalPushState.current = window.history.pushState;
      window.history.pushState = function () {
        originalPushState.current.apply(this, arguments);
        const newPageType = getPageType();
        if (newPageType !== currentPageType) {
          // Reset everything when page type changes
          handlePageTypeChange(newPageType);

          // When changing page types, we need to force a clean slate
          if (
            (currentPageType === 'browse' && newPageType === 'favorites') ||
            (currentPageType === 'favorites' && newPageType === 'browse') ||
            (currentPageType === 'listingForm' && newPageType === 'listings') ||
            (currentPageType === 'browse' && newPageType === 'merchant') ||
            (currentPageType === 'merchant' && newPageType === 'browse')
          ) {
            dispatch(setDataLoading(true)); // Set loading state
            // Small delay to ensure loading state is visible
            setTimeout(() => {
              debouncedSearch(newPageType);
            }, 50);
          } else {
            debouncedSearch(newPageType);
          }
        }
      };

      const handlePopState = () => {
        const newPageType = getPageType();
        if (newPageType !== currentPageType) {
          // Reset everything when page type changes
          handlePageTypeChange(newPageType);

          // When changing page types, we need to force a clean slate
          if (
            (currentPageType === 'browse' && newPageType === 'favorites') ||
            (currentPageType === 'favorites' && newPageType === 'browse') ||
            (currentPageType === 'listingForm' && newPageType === 'listings') ||
            (currentPageType === 'browse' && newPageType === 'merchant') ||
            (currentPageType === 'merchant' && newPageType === 'browse')
          ) {
            dispatch(setDataLoading(true)); // Set loading state
            // Small delay to ensure loading state is visible
            setTimeout(() => {
              debouncedSearch(newPageType);
            }, 50);
          } else {
            debouncedSearch(newPageType);
          }
        }
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (originalPushState.current && window.history.pushState !== originalPushState.current) {
          window.history.pushState = originalPushState.current;
        }
      };
    }
  }, [getPageType, currentPageType, handlePageTypeChange, debouncedSearch]);
};

// Support both named and default exports for backward compatibility
export default useUrlChangeHandler; 